import {
  LabelSmall,
  LabelXSmall,
  LabelSmallBold,
  LabelXSmallBold,
  type RatingSentiment,
} from '@travel-frontend/ui-components';
import {useTranslation, Trans} from 'react-i18next';

import {RatingBadge} from '@/components/RatingBadge';
import {ActionLink} from '@/components/RatingSummary/components/ActionLink';
import {Body} from '@/components/RatingSummary/components/Body';
import {Container} from '@/components/RatingSummary/components/Container';
import {testId} from '@/utils/testId';

type Props = React.ComponentProps<'div'> & {
  size?: 'small' | 'medium';
  compact?: boolean;
  ratingScore: string; // not `number` because rounding should be a concern of the parent component
  ratingScoreMax: string;
  ratingLabel: string;
  ratingSentiment: RatingSentiment;
  reviewCount: number;
  recommendationRate?: number | null;
  onClickReviewCount?: () => void;
};

export const RatingSummary: React.FunctionComponent<Props> = ({
  size = 'medium',
  compact = false,
  ratingScore,
  ratingScoreMax,
  ratingLabel,
  ratingSentiment,
  reviewCount,
  recommendationRate,
  onClickReviewCount,
  ...restProps
}) => {
  const {t} = useTranslation('general');
  const hasRecommendationRate =
    recommendationRate != null && recommendationRate >= 0;
  const RatingLabel = size === 'medium' ? LabelSmallBold : LabelXSmallBold;
  const RecommendationLabel = size === 'medium' ? LabelSmall : LabelXSmall;

  return (
    <Container {...restProps}>
      <div>
        <RatingBadge
          size="medium"
          sentiment={ratingSentiment}
          data-testid={testId('hotel-rating')}
        >
          {t('ratingBadge.label', {ratingScore, ratingScoreMax})}
        </RatingBadge>
      </div>
      <Body>
        <RatingLabel>
          {ratingLabel}
          {reviewCount >= 0 && (
            <>
              &nbsp;
              {typeof onClickReviewCount === 'function' ? (
                <ActionLink onClick={onClickReviewCount}>
                  {t('ratingSummary.reviewCount', {
                    count: reviewCount,
                    context: compact ? 'compact' : '',
                  })}
                </ActionLink>
              ) : (
                t('ratingSummary.reviewCount', {
                  count: reviewCount,
                  context: compact ? 'compact' : '',
                })
              )}
            </>
          )}
        </RatingLabel>
        {hasRecommendationRate && (
          <RecommendationLabel truncate>
            <Trans
              i18nKey="ratingSummary.recommendationRate"
              values={{recommendationRate, maximumFractionDigits: 1}}
            />
          </RecommendationLabel>
        )}
      </Body>
    </Container>
  );
};
